import React, { useCallback, useMemo } from 'react';

import AgGrid from '../../shared/displayComponents/agGrid/agGrid.component';
import useReportParam from '../../shared/reports/useReportParam.hook';
import useReportData from '../../shared/reports/useReportData.hook';
import {
  ActionMenuForExport,
  useActionMenuForExport,
} from '../../shared/displayComponents/actionMenuForExport';
import ReportParamDropdown from '../../shared/reports/reportParamDropdown.component';
import checkIsFetchingAnyParam from '../../shared/reports/checkIsFetchingAnyParam';
import styles from '../../shared/reports/reportParam.module.scss';
import getReportUrl from '../shared/getReportUrl';
import getExportMenuItems from '../shared/getExportMenuItems';

import columnDefinitions from './everywhereBreakdown.columnDefinitions';
import { reportDataFormatter } from './utils';

const EverywhereBreakdown = () => {
  const endpointBasePath = getReportUrl('everywhere-breakdown');

  const taxYearParam = useReportParam({
    endpointBasePath,
    paramName: 'taxYear',
    params: useMemo(() => ({}), []),
  });

  const periodParam = useReportParam({
    endpointBasePath,
    paramName: 'period',
    params: useMemo(
      () => ({
        taxYear: taxYearParam.paramValue,
      }),
      [taxYearParam.paramValue],
    ),
  });

  const entityIdParam = useReportParam({
    endpointBasePath,
    paramName: 'entityId',
    params: useMemo(
      () => ({
        taxYear: taxYearParam.paramValue,
        period: periodParam.paramValue,
      }),
      [taxYearParam.paramValue, periodParam.paramValue],
    ),
  });

  const jurisdictionIdParam = useReportParam({
    endpointBasePath,
    paramName: 'jurisdictionId',
    params: useMemo(
      () => ({
        taxYear: taxYearParam.paramValue,
        period: periodParam.paramValue,
      }),
      [taxYearParam.paramValue, periodParam.paramValue],
    ),
  });

  const categoryIdParam = useReportParam({
    endpointBasePath,
    paramName: 'categoryId',
    params: useMemo(() => ({}), []),
  });

  const isFetchingAnyParam = checkIsFetchingAnyParam([
    taxYearParam,
    periodParam,
    entityIdParam,
    jurisdictionIdParam,
    categoryIdParam,
  ]);

  const { reportData, isFetchingReportData, runReportButton, showGrid } = useReportData({
    endpointBasePath,
    method: 'POST',
    isFetchingAnyParam,
    params: useMemo(
      () => ({
        taxYear: taxYearParam.paramValue,
        period: periodParam.paramValue,
        orgId: entityIdParam.paramValue,
        jurisdictionId: jurisdictionIdParam.paramValue,
        categoryId: categoryIdParam.paramValue,
      }),
      [
        taxYearParam.paramValue,
        periodParam.paramValue,
        entityIdParam.paramValue,
        jurisdictionIdParam.paramValue,
        categoryIdParam.paramValue,
      ],
    ),
  });

  const getContextMenuItems = useCallback(getExportMenuItems, []);
  const { props, onGridReady } = useActionMenuForExport({ reportData });

  return (
    <>
      <div className="row">
        <div className={`col ${styles.reportParam}`}>
          <ReportParamDropdown label="Tax Year" param={taxYearParam} />
          <ReportParamDropdown label="Period" param={periodParam} />
          <ReportParamDropdown label="Entity" param={entityIdParam} />
          <ReportParamDropdown label="Jurisdiction" param={jurisdictionIdParam} />
          <ReportParamDropdown label="Category" param={categoryIdParam} />
          {runReportButton}
        </div>
      </div>
      <ActionMenuForExport {...props} />
      <div className="row grid-row">
        <div className="col">
          {showGrid && (
            <AgGrid
              rowData={reportDataFormatter(reportData, entityIdParam)}
              columnDefs={columnDefinitions}
              isGridLoading={isFetchingReportData}
              getContextMenuItems={getContextMenuItems}
              onGridReady={onGridReady}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default EverywhereBreakdown;
